import React, {useEffect, useRef} from "react";
import cn from "classnames";
import {createUseStyles} from "react-jss";
import {primaryTextColor} from "~/theme";
import useRenderId from "~/hooks/useRenderId";
import UILink from "~/old-ui/UILink";

export default function UITable({responsive, fixedLeft, className, children, ...props}) {
  const classes = useStyles();

  const ref = useRef();

  const [renderId, updateRenderId] = useRenderId();

  useEffect(() => {
    if (responsive && fixedLeft > 0) {
      ref.current.querySelectorAll('.' + classes.row).forEach(
        (row) => {
          const cells = row.querySelectorAll('.' + classes.cell);
          let currentLeftOffset = 0;

          for (let i = 0; i < cells.length; i++) {
            const cell = cells[i];

            if (i < fixedLeft) {
              cell.classList.add(classes.fixedCell);

              cell.style.left = currentLeftOffset + 'px';
              currentLeftOffset += cell.offsetWidth;
            } else {
              cell.classList.remove(classes.fixedCell);
              cell.style.left = null;
            }
          }
        }
      )
    }
  }, [renderId, responsive, fixedLeft, classes.fixedCell, classes.row, classes.cell, children]);

  useEffect(() => {
    const eventListener = () => updateRenderId();
    if (responsive) {
      window.addEventListener('resize', eventListener);
    }

    return () => window.removeEventListener('resize', eventListener);
  }, [responsive, updateRenderId]);

  return (
    <div className={classes.wrapper}>
      <table ref={ref} className={cn(classes.table, {
        [classes.responsiveTable]: responsive
      }, className)
      } {...props}>
        {children}
      </table>
    </div>
  )
}

UITable.defaultProps = {
  fixedLeft: 1,
};


export function UITableBody({className, ...props}) {
  const classes = useStyles();
  return (<tbody className={cn(classes.tbody, className)} {...props}/>)
}

export function UITableHead({className, ...props}) {
  const classes = useStyles();
  return (<thead className={cn(classes.thead, className)} {...props}/>)
}

export function UITableRow({className, ...props}) {
  const classes = useStyles();
  return (<tr className={cn(classes.row, className)} {...props}/>)
}

export function UITableCell({primary, noWrap, narrow, className, ...props}) {
  const classes = useStyles();
  return (<td className={cn(classes.cell, {
    [classes.primaryCell]: primary,
    [classes.narrowCell]: narrow,
    [classes.noWrapCell]: noWrap,
  }, className)} {...props}/>)
}


export function UITableLinkCell({component: Component, primary, narrow, noWrap, className, children, ...props}) {
  const classes = useStyles();

  return (
    <UITableCell className={className} primary={primary} narrow noWrap={noWrap}>
      <Component className={cn(classes.cellLink, {[classes.narrowCellLink]: narrow})} {...props}>
        {children}
      </Component>
    </UITableCell>
  )
}

UITableLinkCell.defaultProps = {
  component: UILink,
};


const useStyles = createUseStyles({
  wrapper: {
    width: '100%',
    overflowX: 'scroll',
  },

  table: {
    width: '100%',

    borderSpacing: 0,
    borderCollapse: 'collapse',
  },

  responsiveTable: {
    position: 'relative',
  },

  thead: {
    '& $cell': {
      backgroundColor: '#e7e7e7',
      color: primaryTextColor,
      fontWeight: 'bold',
    }
  },

  tbody: {},

  row: {
    '& + & > $cell': {
      borderTop: [[1, 'solid', '#E7E7E7']],
    }
  },

  cell: {
    boxSizing: 'border-box',
    padding: [[10, 16]],
    backgroundColor: '#fff',
    color: primaryTextColor,
  },

  narrowCell: {
    padding: 0,
  },

  noWrapCell: {
    whiteSpace: 'nowrap',
  },

  cellLink: {
    display: 'block',
    color: 'inherit',
    padding: [[10, 16]],
    border: 0,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },

  cellLinkNarrow: {
    padding: 0,
  },

  fixedCell: {
    position: 'sticky',
    left: 0,
    zIndex: 10,
  },

  primaryCell: {
    width: '60%',
  }
});
